import classNames from 'classnames';
import debounce from 'debounce-promise';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { GroupBase, StylesConfig } from 'react-select-modern';

import type { Event } from '@feathr/blackbox';
import type { IFormElementProps, ISelectOption } from '@feathr/components';
import { UniversalSelect } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { DEFAULT_DEBOUNCE_WAIT } from '@feathr/hooks';
import type { Attributes, IListParams } from '@feathr/rachis';

import type { IEventFilters } from '../EventSwitcher/EventSwitcher';

import * as styles from './EventSelect.css';

interface IProps extends IFormElementProps {
  className?: string;
  /** Exclude this project ID from results (useful when moving campaigns) */
  excludeId?: string;
  id?: string;
  /** Controls dropdown menu placement. Use 'auto' to flip when space is limited. */
  menuPlacement?: 'auto' | 'bottom' | 'top';
  /** Render dropdown in a portal (useful inside modals to prevent clipping) */
  menuPortalTarget?: HTMLElement | null;
  onChange: (newValue: ISelectOption) => void;
  onClear?: () => void;
  placeholder?: string;
  /** Custom styles for react-select (e.g., for z-index on menuPortal) */
  selectStyles?: StylesConfig<ISelectOption, boolean, GroupBase<ISelectOption>>;
  value: ISelectOption | undefined;
}

function EventSelect({
  className,
  excludeId,
  id,
  helpText,
  label,
  menuPlacement,
  menuPortalTarget,
  onChange,
  onClear,
  placeholder,
  selectStyles,
  value,
}: IProps): JSX.Element {
  const { Events } = useStore();
  const { t } = useTranslation();

  const loadPagedOptions = useCallback(
    async (
      inputValue: string,
      prevOptions: readonly ISelectOption[],
      additional?: { page: number },
    ): Promise<{
      options: readonly ISelectOption[];
      hasMore: boolean;
      additional: { page: number };
    }> => {
      const currentPage = additional?.page ?? 0;

      const listParams: IListParams<Attributes<Event>> = {
        filters: {
          is_archived__ne: true,
        } as IEventFilters,
        only: ['id', 'logo', 'name'],
        ordering: ['name'],
        pagination: {
          page: currentPage,
          page_size: UniversalSelect.DEFAULT_PAGE_SIZE,
        },
      };
      if (excludeId) {
        listParams.filters!.id__ne = excludeId;
      }
      if (inputValue.length) {
        listParams.filters!.name__icontains = inputValue;
      }
      const data = Events.list(listParams);
      await when(() => !data.isPending);
      const loadedOptions: ISelectOption[] = data.models.map((event: Event) => ({
        id: event.id,
        name: event.name,
      }));
      const options = [...loadedOptions];

      const hasMore = loadedOptions.length === UniversalSelect.DEFAULT_PAGE_SIZE;

      return {
        options,
        hasMore,
        additional: {
          page: currentPage + 1,
        },
      };
    },
    [Events, excludeId],
  );

  const debouncedLoadOptions = useMemo(
    () => debounce(loadPagedOptions, DEFAULT_DEBOUNCE_WAIT),
    [loadPagedOptions],
  );

  function handleChange(eventSelected: ISelectOption): void {
    onChange(eventSelected);
  }

  function handleOptionsMessage({ inputValue }): string {
    return inputValue.length ? t('No results...') : t('Start typing the name of a project...');
  }

  return (
    <UniversalSelect<ISelectOption>
      cacheOptions={true}
      defaultOptions={true}
      helpText={helpText}
      id={id}
      label={label}
      loadPagedOptions={debouncedLoadOptions}
      menuPlacement={menuPlacement}
      menuPortalTarget={menuPortalTarget}
      name={'event_select'}
      noOptionsMessage={handleOptionsMessage}
      onClear={onClear}
      onSelectSingle={handleChange}
      paginate={true}
      placeholder={placeholder}
      styles={selectStyles}
      value={value}
      wrapperClassName={classNames(styles.root, className)}
    />
  );
}

export default observer(EventSelect);
